import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { validateDocument, validateIban, validateEmail, validatePhone } from "../utils/documentValidation";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";  // eslint-disable-line no-unused-vars
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";  // eslint-disable-line no-unused-vars
import { toast } from "sonner";
import {
  splitTechnicalInfoByTemplate,  // eslint-disable-line no-unused-vars
  resolveTechnicalTemplate,  // eslint-disable-line no-unused-vars
  resolveTemplateLabel,  // eslint-disable-line no-unused-vars
} from "../lib/technicalFieldTemplates";
import {
  ArrowLeft,
  Building2,
  Home,
  Clock,
  Network,
  FileText,
  User,
  Phone,
  Mail,
  Calendar,
  Trash2,
  Eye,
  EyeOff,
  DollarSign,
  TrendingUp,
  FolderKanban,
  Link2,
  Unlink,
  AlertTriangle,
  Lock,  // eslint-disable-line no-unused-vars
  Shield,  // eslint-disable-line no-unused-vars
  Wrench,
} from "lucide-react";
// P2 refactor: pure helpers + visual constants moved to a shared module so
// the upcoming tab/modal sub-components can consume the same rules without
// duplication. See `components/customer/utils.js`.
import {
  formatDateES,
  serviceStatusColors,
} from "../components/customer/utils";

// P2 refactor step 2: "Detalle del servicio" modal — >500 líneas movidas a
// su propio componente. Recibe estado + handlers como props; no cambia
// funcionalidad ni estilo.
import { ServiceDetailDialog } from "../components/customer/ServiceDetailDialog";
// P2 refactor step 3: Edit-location + New/Edit-project modals.
import { EditLocationModal } from "../components/customer/EditLocationModal";
import { ProjectModal, EditProjectModal } from "../components/customer/ProjectModal";
// P2 refactor step 4a: Historial tab.
import { HistorialTab } from "../components/customer/HistorialTab";
// P2 refactor step 4b/c: Oportunidades + Proyectos tabs.
import { OportunidadesTab } from "../components/customer/OportunidadesTab";
import { ProyectosTab } from "../components/customer/ProyectosTab";
// P2 refactor step 4d/e: Ubicaciones + Servicios tabs.
import { UbicacionesTab } from "../components/customer/UbicacionesTab";
import { ServiciosTab } from "../components/customer/ServiciosTab";
// P2 refactor step 4f (cierre): Datos tab.
import { DatosTab } from "../components/customer/DatosTab";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canEdit } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [locations, setLocations] = useState([]);
  const [services, setServices] = useState([]);
  // Grouping state for multi-extension services (e.g. Centralita 3x → 3 rows).
  // Key = parent_linea_id (new rows) or legacy bucket key (provision_id::catalog_service_id).
  const [expandedServiceGroups, setExpandedServiceGroups] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  const [catalogServices, setCatalogServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [opportunities, setOpportunities] = useState([]);
  const [users, setUsers] = useState([]);

  // Inline validation feedback for the editable identity document + IBAN
  // fields. The hooks below recompute the validation result every keystroke
  // using the SAME shared rules the backend enforces (ImportPath:
  // utils/documentValidation.js mirrors backend/services/document_validation.py).
  // The result is purely cosmetic — submission still goes through the
  // backend and any final disagreement is surfaced as a toast.
  const docValidation = useMemo(() => {
    if (!customer) return null;
    const value = customer.customer_type === "EMPRESA" ? editData.cif : editData.dni_nie;
    return validateDocument(customer.customer_type, editData.document_type, value);
  }, [customer, editData.cif, editData.dni_nie, editData.document_type]);
  const ibanValidation = useMemo(() => validateIban(editData.iban), [editData.iban]);
  const emailValidation = useMemo(() => validateEmail(editData.email), [editData.email]);
  const phoneValidation = useMemo(
    () => validatePhone(editData.phone, editData.phone_country || "ES"),
    [editData.phone, editData.phone_country],
  );
  // Representative document is empresa-only and uses personal-doc rules.
  const repDocValidation = useMemo(() => {
    if (customer?.customer_type !== "EMPRESA") return null;
    return validateDocument("RESIDENCIAL", editData.representante_document_type, editData.dni_representante);
  }, [customer, editData.representante_document_type, editData.dni_representante]);
  
  // Modals
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [serviceDetailOpen, setServiceDetailOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, id: null, name: "" });
  
  // Technical info state. Holds the full `informacion_tecnica` object so
  // unknown / category-specific / legacy keys are never dropped between load
  // and save. Rendering selects fields via detectServiceType(service) →
  // TECHNICAL_FIELD_TEMPLATES[type].
  const [techInfoExpanded, setTechInfoExpanded] = useState(false);
  const [technicalInfo, setTechnicalInfo] = useState({});
  const [loadingTechInfo, setLoadingTechInfo] = useState(false);
  const [serviceCategories, setServiceCategories] = useState([]);
  
  // New location form
  const [newLocation, setNewLocation] = useState({
    nombre_sede: "",
    direccion_completa: "",
    codigo_postal: "",
    ciudad: "",
    contacto_tecnico_sede: "",
    telefono_sede: "",
    informacion_adicional: "",
  });
  
  // Edit location state
  const [editLocationModalOpen, setEditLocationModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);

  // Projects state
  const [projects, setProjects] = useState([]);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [editProjectModalOpen, setEditProjectModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [newProject, setNewProject] = useState({
    name: "",
    description: "",
    service_ids: [],
  });

  // Open tickets state (for alert)
  const [openTickets, setOpenTickets] = useState({ incidents: 0, requests: 0 });

  // Reset technical-info state every time the selected service changes so the
  // previous service's values never leak into a sibling. The collapsible may
  // already be open from the previous service, in which case Radix's
  // `onOpenChange` would NOT fire on `true → true` and `loadTechnicalInfo`
  // would never run, leading to two services appearing to share SIM/router
  // data. Owning the lifecycle here also covers the pre-expand state when the
  // user opens a different service without collapsing first.
  useEffect(() => {
    if (!selectedService?.id) {
      setTechnicalInfo({});
      setTechInfoExpanded(false);
      return;
    }
    setTechnicalInfo({});
    if (techInfoExpanded && canViewTechnicalInfo()) {
      loadTechnicalInfo(selectedService.id);
    }
    // Intentionally exclude `techInfoExpanded` and helpers — the effect must
    // re-run only when the *service identity* changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService?.id]);

  const fetchData = useCallback(async () => {
    try {
      const safe = (promise, fallback) => promise.catch(() => ({ data: fallback }));
      const [customerRes, locationsRes, servicesRes, auditRes, catalogRes, oppsRes, comercialesRes, projectsRes, openIncidentsRes, serviceCategoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/customers/${id}`),
        safe(axios.get(`${API_URL}/api/locations?customer_id=${id}`), []),
        safe(axios.get(`${API_URL}/api/customer-services?customer_id=${id}`), []),
        safe(axios.get(`${API_URL}/api/audit-logs/customer/${id}`), []),
        safe(axios.get(`${API_URL}/api/catalog-services`), []),
        safe(axios.get(`${API_URL}/api/opportunities?customer_id=${id}`), []),
        safe(axios.get(`${API_URL}/api/users/comerciales`), []),
        safe(axios.get(`${API_URL}/api/projects?customer_id=${id}`), []),
        safe(axios.get(`${API_URL}/api/customers/${id}/open-incidents`), { incidents: 0, requests: 0 }),
        safe(axios.get(`${API_URL}/api/service-categories`), []),
      ]);

      setCustomer(customerRes.data);
      setLocations(locationsRes.data);
      setServices(servicesRes.data);
      setAuditLogs(auditRes.data);
      setCatalogServices(catalogRes.data);
      setOpportunities(oppsRes.data);
      setUsers(comercialesRes.data);
      setProjects(projectsRes.data);
      setEditData(customerRes.data);
      setOpenTickets(openIncidentsRes.data);
      setServiceCategories(serviceCategoriesRes.data);
    } catch (error) {
      console.error("Error fetching customer data:", error);
      toast.error("Error al cargar datos del cliente");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    try {
      await axios.put(`${API_URL}/api/customers/${id}`, editData);
      setCustomer(editData);
      setEditing(false);
      toast.success("Cliente actualizado");
    } catch (error) {
      console.error("Error updating customer:", error);
      toast.error("Error al actualizar cliente");
    }
  };

  const handleCreateLocation = async () => {
    if (!newLocation.nombre_sede || !newLocation.direccion_completa) {
      toast.error("Nombre y dirección son obligatorios");
      return;
    }

    try {
      await axios.post(`${API_URL}/api/locations`, {
        ...newLocation,
        customer_id: id,
      });
      toast.success("Ubicación creada");
      setLocationModalOpen(false);
      setNewLocation({
        nombre_sede: "",
        direccion_completa: "",
        codigo_postal: "",
        ciudad: "",
        contacto_tecnico_sede: "",
        telefono_sede: "",
        informacion_adicional: "",
      });
      fetchData();
    } catch (error) {
      console.error("Error creating location:", error);
      toast.error("Error al crear ubicación");
    }
  };

  const handleUpdateService = async () => {
    if (!selectedService) return;
    
    if (selectedService.descuento_tipo === "OTROS" && !selectedService.descuento_concepto) {
      toast.error("El concepto de descuento es obligatorio cuando el tipo es 'Otros'");
      return;
    }

    try {
      // Persist general service edits (notes, pricing, discount, …).
      await axios.put(`${API_URL}/api/customer-services/${selectedService.id}`, selectedService);

      // If the current user is allowed to edit technical info AND the
      // Información Técnica section has been expanded (i.e. loaded and
      // potentially edited), also persist the technical dict in the same
      // "Guardar Cambios" click. Preserves unknown/legacy keys via the
      // backend merge semantics. Technical info is never sent for users
      // without permission — the section is gated by canViewTechnicalInfo.
      if (canViewTechnicalInfo() && techInfoExpanded) {
        try {
          await axios.put(
            `${API_URL}/api/customer-services/${selectedService.id}/technical-info`,
            technicalInfo
          );
        } catch (techErr) {
          console.error("Error saving technical info:", techErr);
          toast.error(
            techErr.response?.data?.detail || "Error al guardar información técnica"
          );
          return; // Stop: keep modal open so user can retry technical fields.
        }
      }

      toast.success("Servicio actualizado");
      setServiceDetailOpen(false);
      setSelectedService(null);
      fetchData();
    } catch (error) {
      console.error("Error updating service:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar servicio");
    }
  };

  const loadTechnicalInfo = async (serviceId) => {
    setLoadingTechInfo(true);
    try {
      const res = await axios.get(`${API_URL}/api/customer-services/${serviceId}/technical-info`);
      // Store the full response so category-specific and unknown legacy keys
      // round-trip through save without loss. Values come already decrypted
      // (or masked) according to the user's role.
      setTechnicalInfo(res.data || {});
    } catch (error) {
      console.error("Error loading technical info:", error);
    } finally {
      setLoadingTechInfo(false);
    }
  };

  // Technical info is persisted through handleUpdateService() alongside the
  // rest of the service fields in a single "Guardar Cambios" click.

  // Check if user can view/edit technical info
  const canViewTechnicalInfo = () => {
    return user && user.roles?.some(r => r === "SUPERADMIN" || r === "TECNICO");
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await axios.delete(`${API_URL}/api/customer-services/${serviceId}`);
      toast.success("Servicio eliminado");
      setDeleteConfirm({ open: false, type: null, id: null, name: "" });
      fetchData();
    } catch (error) {
      console.error("Error deleting service:", error);
      toast.error("Error al eliminar servicio");
    }
  };

  const handleUpdateLocation = async () => {
    if (!editingLocation) return;
    
    try {
      await axios.put(`${API_URL}/api/locations/${editingLocation.id}`, editingLocation);
      toast.success("Ubicación actualizada");
      setEditLocationModalOpen(false);
      setEditingLocation(null);
      fetchData();
    } catch (error) {
      console.error("Error updating location:", error);
      toast.error("Error al actualizar ubicación");
    }
  };

  const handleDeleteLocation = async (locationId) => {
    try {
      await axios.delete(`${API_URL}/api/locations/${locationId}`);
      toast.success("Ubicación eliminada");
      setDeleteConfirm({ open: false, type: null, id: null, name: "" });
      fetchData();
    } catch (error) {
      console.error("Error deleting location:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar ubicación");
    }
  };

  const openEditLocation = (location) => {
    setEditingLocation({ ...location });
    setEditLocationModalOpen(true);
  };

  // Project handlers
  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast.error("El nombre del proyecto es obligatorio");
      return;
    }

    try {
      await axios.post(`${API_URL}/api/projects`, {
        customer_id: id,
        name: newProject.name,
        description: newProject.description,
        service_ids: newProject.service_ids,
      });
      toast.success("Proyecto creado correctamente");
      setProjectModalOpen(false);
      setNewProject({ name: "", description: "", service_ids: [] });
      fetchData();
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error(error.response?.data?.detail || "Error al crear proyecto");
    }
  };

  const handleUpdateProject = async () => {
    if (!selectedProject || !selectedProject.name.trim()) {
      toast.error("El nombre del proyecto es obligatorio");
      return;
    }

    try {
      await axios.put(`${API_URL}/api/projects/${selectedProject.id}`, {
        name: selectedProject.name,
        description: selectedProject.description,
        service_ids: selectedProject.service_ids,
      });
      toast.success("Proyecto actualizado correctamente");
      setEditProjectModalOpen(false);
      setSelectedProject(null);
      fetchData();
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar proyecto");
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await axios.delete(`${API_URL}/api/projects/${projectId}`);
      toast.success("Proyecto eliminado");
      fetchData();
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar proyecto");
    }
  };

  const openEditProject = (project) => {
    setSelectedProject({ ...project });
    setEditProjectModalOpen(true);
  };

  const getServicesNotInProject = () => {
    const projectServiceIds = projects.flatMap(p => p.service_ids || []);
    return services.filter(s => !projectServiceIds.includes(s.id) && s.estado === "ACTIVO");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
    }).format(value || 0);
  };

  const getCustomerDisplayName = () => {
    if (!customer) return "";
    if (customer.customer_type === "EMPRESA") {
      return customer.razon_social || "";
    }
    return `${customer.nombre || ""} ${customer.apellidos || ""}`.trim();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  // Group multi-extension services (e.g. Centralita 3x → 3 customer_services)
  // AND nest any `asociado_a_linea_id` add-on (extension bonos, mobile line
  // bonos, Fibra IP fija, …) under the correct parent row. Relation uses the
  // `asociado_a_linea_id` / `parent_linea_id` fields exclusively — no name
  // parsing, no shape guessing.
  //
  // Grouping rule: ONLY group when `parent_linea_id` is explicitly set by the
  // backend at provisioning time (true multi-unit products like Centralita
  // 3x or "(2x) Móvil 25GB"). Two independent selections of the same
  // catalog product with `unidades_por_seleccion=1` must remain as separate
  // rows, even if they share product+provision — the previous heuristic
  // "more than 1 row sharing (provision_id, catalog_service_id) → group as
  // legacy extensions" misclassified independent lines as
  // "Ext 1/2 (histórico)".
  const buildGroupedServices = (list) => {
    if (!Array.isArray(list) || list.length === 0) return [];

    // Split associated add-ons from standalone services. Add-ons are indexed
    // by their parent linea_id so we can attach them under the right row.
    const addonsByParent = {};
    const standalone = [];
    for (const s of list) {
      if (s.asociado_a_linea_id) {
        if (!addonsByParent[s.asociado_a_linea_id]) addonsByParent[s.asociado_a_linea_id] = [];
        addonsByParent[s.asociado_a_linea_id].push(s);
      } else {
        standalone.push(s);
      }
    }

    // Multi-unit grouping — only when the backend explicitly stamps
    // `parent_linea_id` on the children. Anything else is an independent row.
    const parentGroups = new Map();
    const singles = [];
    for (const s of standalone) {
      if (s.parent_linea_id) {
        if (!parentGroups.has(s.parent_linea_id)) parentGroups.set(s.parent_linea_id, []);
        parentGroups.get(s.parent_linea_id).push(s);
      } else {
        singles.push(s);
      }
    }

    // Determine which linea_ids are actually displayed (singles + group children)
    // so we can detect add-ons whose parent is missing and fall them back to
    // top-level instead of hiding them silently.
    const displayedLineaIds = new Set();
    singles.forEach(s => { if (s.linea_id) displayedLineaIds.add(s.linea_id); });
    parentGroups.forEach(children => children.forEach(c => { if (c.linea_id) displayedLineaIds.add(c.linea_id); }));

    Object.keys(addonsByParent).forEach(parentId => {
      if (!displayedLineaIds.has(parentId)) {
        // Orphan add-ons: their parent row isn't visible (e.g. parent was
        // removed or data is inconsistent). Promote to top-level singles so
        // the user still sees them.
        singles.push(...addonsByParent[parentId]);
        delete addonsByParent[parentId];
      }
    });

    const pick = (lid) => addonsByParent[lid] || [];
    const result = [];
    singles.forEach(s => result.push({ type: "single", service: s, addons: pick(s.linea_id) }));
    parentGroups.forEach((children, key) => {
      children.sort((a, b) => (a.linea_numero || 0) - (b.linea_numero || 0));
      const decorated = children.map(c => ({ ...c, _addons: pick(c.linea_id) }));
      result.push({ type: "group", key, children: decorated });
    });
    return result;
  };
  const groupedServices = buildGroupedServices(services);

  // Small helper for rendering an associated add-on row (extension bono, mobile
  // line bono, IP fija, …) nested under its parent row. `indentLevel` controls
  // padding so we can stack addon → parent child → group header cleanly.
  const renderAddonRow = (addon, keyPrefix, indentLevel = 1) => (
    <TableRow
      key={`${keyPrefix}-addon-${addon.id}`}
      className="border-[#1E1E5F] hover:bg-[#1B1B4B]/30 cursor-pointer bg-[#0A0A35]/30"
      data-testid={`addon-row-${addon.id}`}
      onClick={() => { setSelectedService(addon); setServiceDetailOpen(true); }}
    >
      <TableCell className={`text-cyan-300/80 text-xs ${indentLevel >= 2 ? 'pl-16' : 'pl-10'}`}>
        └─ {addon.nombre_servicio}
      </TableCell>
      <TableCell className="text-slate-400 text-xs">{addon.proveedor_nombre || addon.proveedor || "-"}</TableCell>
      <TableCell className="text-slate-400 text-xs">{formatCurrency(addon.precio_sin_iva)}</TableCell>
      <TableCell className="text-cyan-300/80 text-xs">{formatCurrency(addon.precio_final)}</TableCell>
      <TableCell className="text-slate-400 text-xs">{formatCurrency(addon.descuento_monto)}</TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell><Badge className={serviceStatusColors[addon.estado]}>{addon.estado}</Badge></TableCell>
      <TableCell className="text-slate-400 text-xs">{formatDateES(addon.fecha_alta)}</TableCell>
      <TableCell>
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" onClick={() => { setSelectedService(addon); setServiceDetailOpen(true); }}
            className="text-[#03BEE4] hover:text-[#03BEE4]/80 h-8 w-8 p-0" title="Ver detalle">
            <Eye className="h-4 w-4" />
          </Button>
          {/* Add-on rows represent real customer_service records, so they
              expose the same Incidencia / Solicitud shortcuts as normal
              rows. Actions use the add-on's OWN id, not the parent's. */}
          <Button size="sm" variant="ghost"
            onClick={() => { navigate(`/incidents?action=create&customer=${id}&service=${addon.id}`); }}
            className="text-red-400 hover:text-red-300 h-8 w-8 p-0" title="Crear Incidencia"
            data-testid={`addon-incident-${addon.id}`}>
            <AlertTriangle className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost"
            onClick={() => { navigate(`/requests?action=create&customer=${id}&service=${addon.id}`); }}
            className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0" title="Crear Solicitud"
            data-testid={`addon-request-${addon.id}`}>
            <FileText className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost"
            onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ open: true, type: "service", id: addon.id, name: addon.nombre_servicio }); }}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0" title="Eliminar"
            data-testid={`delete-service-${addon.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  if (!customer) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl text-white">Cliente no encontrado</h2>
        <Button onClick={() => navigate("/customers")} className="mt-4">
          Volver a Clientes
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      {/* Header */}
      <div className="bg-[#0A0A35] border border-[#1E1E5F] rounded-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/customers")}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-heading font-semibold text-white">
                Cliente: {getCustomerDisplayName()}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-slate-400">Tipo de Cliente:</span>
                <div className="flex items-center gap-2">
                  {customer.customer_type === "EMPRESA" ? (
                    <Building2 className="h-4 w-4 text-[#03BEE4]" />
                  ) : (
                    <Home className="h-4 w-4 text-purple-400" />
                  )}
                  <span className="text-white">
                    {customer.customer_type === "EMPRESA" ? "Empresa" : "Residencial"}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <Button
            onClick={() => navigate("/customers")}
            className="bg-[#03BEE4] text-white hover:bg-[#03BEE4]/80"
          >
            Volver
          </Button>
        </div>
      </div>

      {/* Alert for open tickets */}
      {(openTickets.incidents > 0 || openTickets.requests > 0) && (
        <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-red-400 font-semibold">
              Tickets Abiertos
            </p>
            <p className="text-red-300 text-sm">
              Este cliente tiene 
              {openTickets.incidents > 0 && (
                <span className="font-bold"> {openTickets.incidents} incidencia(s)</span>
              )}
              {openTickets.incidents > 0 && openTickets.requests > 0 && " y"}
              {openTickets.requests > 0 && (
                <span className="font-bold"> {openTickets.requests} solicitud(es)</span>
              )}
              {" "}pendientes de resolver.
            </p>
          </div>
          <div className="flex gap-2">
            {openTickets.incidents > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/incidents?customer=${id}`)}
                className="border-red-500 text-red-400 hover:bg-red-500/20"
              >
                Ver Incidencias
              </Button>
            )}
            {openTickets.requests > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/requests?customer=${id}`)}
                className="border-orange-500 text-orange-400 hover:bg-orange-500/20"
              >
                Ver Solicitudes
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="datos" className="space-y-6">
        <TabsList className="bg-[#0A0A35] border border-[#1E1E5F] p-1">
          <TabsTrigger
            value="datos"
            className="data-[state=active]:bg-[#42E695] data-[state=active]:text-[#04042C]"
          >
            Datos del cliente
          </TabsTrigger>
          <TabsTrigger
            value="servicios"
            className="data-[state=active]:bg-[#42E695] data-[state=active]:text-[#04042C]"
          >
            Servicios
          </TabsTrigger>
          <TabsTrigger
            value="ubicaciones"
            className="data-[state=active]:bg-[#42E695] data-[state=active]:text-[#04042C]"
          >
            Ubicaciones
          </TabsTrigger>
          <TabsTrigger
            value="oportunidades"
            className="data-[state=active]:bg-[#42E695] data-[state=active]:text-[#04042C]"
          >
            Oportunidades
            {opportunities.filter(o => !["GANADA", "PERDIDA"].includes(o.stage)).length > 0 && (
              <Badge className="ml-2 bg-[#03BEE4] text-white text-xs">
                {opportunities.filter(o => !["GANADA", "PERDIDA"].includes(o.stage)).length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="proyectos"
            className="data-[state=active]:bg-[#42E695] data-[state=active]:text-[#04042C]"
          >
            Proyectos
            {projects.length > 0 && (
              <Badge className="ml-2 bg-purple-500 text-white text-xs">
                {projects.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="historial"
            className="data-[state=active]:bg-[#42E695] data-[state=active]:text-[#04042C]"
          >
            Historial
          </TabsTrigger>
        </TabsList>

        {/* Datos del Cliente Tab */}
        <TabsContent value="datos" className="space-y-6">
          <DatosTab
            customer={customer}
            editing={editing}
            setEditing={setEditing}
            editData={editData}
            setEditData={setEditData}
            users={users}
            services={services}
            docValidation={docValidation}
            repDocValidation={repDocValidation}
            phoneValidation={phoneValidation}
            emailValidation={emailValidation}
            ibanValidation={ibanValidation}
            handleSave={handleSave}
            canEdit={canEdit}
            formatDateTime={formatDateTime}
          />
        </TabsContent>

        {/* Servicios Tab */}
        <TabsContent value="servicios" className="space-y-4">
          <ServiciosTab
            services={services}
            groupedServices={groupedServices}
            renderAddonRow={renderAddonRow}
            expandedServiceGroups={expandedServiceGroups}
            setExpandedServiceGroups={setExpandedServiceGroups}
            setSelectedService={setSelectedService}
            setServiceDetailOpen={setServiceDetailOpen}
            setDeleteConfirm={setDeleteConfirm}
            navigate={navigate}
            customerId={id}
            formatCurrency={formatCurrency}
          />
        </TabsContent>


        {/* Ubicaciones Tab */}
        <TabsContent value="ubicaciones" className="space-y-4">
          <UbicacionesTab
            locations={locations}
            locationModalOpen={locationModalOpen}
            setLocationModalOpen={setLocationModalOpen}
            newLocation={newLocation}
            setNewLocation={setNewLocation}
            handleCreateLocation={handleCreateLocation}
            openEditLocation={openEditLocation}
            setDeleteConfirm={setDeleteConfirm}
            formatDateTime={formatDateTime}
          />
        </TabsContent>

        {/* Oportunidades Tab */}
        <TabsContent value="oportunidades" className="space-y-4">
          <OportunidadesTab
            opportunities={opportunities}
            formatCurrency={formatCurrency}
            navigate={navigate}
          />
        </TabsContent>

        {/* Proyectos Tab */}
        <TabsContent value="proyectos" className="space-y-4">
          <ProyectosTab
            projects={projects}
            services={services}
            setProjectModalOpen={setProjectModalOpen}
            openEditProject={openEditProject}
            handleDeleteProject={handleDeleteProject}
          />
        </TabsContent>

        {/* Historial Tab */}
        <TabsContent value="historial" className="space-y-4">
          <HistorialTab
            auditLogs={auditLogs}
            services={services}
            locations={locations}
            setSelectedService={setSelectedService}
            setServiceDetailOpen={setServiceDetailOpen}
            openEditLocation={openEditLocation}
            formatDateTime={formatDateTime}
          />
        </TabsContent>
      </Tabs>

      {/* Service Detail Modal */}
      <ServiceDetailDialog
        open={serviceDetailOpen}
        onOpenChange={setServiceDetailOpen}
        selectedService={selectedService}
        setSelectedService={setSelectedService}
        locations={locations}
        catalogServices={catalogServices}
        serviceCategories={serviceCategories}
        formatCurrency={formatCurrency}
        canViewTechnicalInfo={canViewTechnicalInfo}
        techInfoExpanded={techInfoExpanded}
        setTechInfoExpanded={setTechInfoExpanded}
        loadTechnicalInfo={loadTechnicalInfo}
        loadingTechInfo={loadingTechInfo}
        technicalInfo={technicalInfo}
        setTechnicalInfo={setTechnicalInfo}
        handleUpdateService={handleUpdateService}
      />

      {/* Edit Location Modal */}
      <EditLocationModal
        open={editLocationModalOpen}
        onOpenChange={setEditLocationModalOpen}
        editingLocation={editingLocation}
        setEditingLocation={setEditingLocation}
        handleUpdateLocation={handleUpdateLocation}
      />

      {/* New Project Modal */}
      <ProjectModal
        open={projectModalOpen}
        onOpenChange={setProjectModalOpen}
        newProject={newProject}
        setNewProject={setNewProject}
        getServicesNotInProject={getServicesNotInProject}
        handleCreateProject={handleCreateProject}
      />

      {/* Edit Project Modal */}
      <EditProjectModal
        open={editProjectModalOpen}
        onOpenChange={setEditProjectModalOpen}
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
        services={services}
        handleUpdateProject={handleUpdateProject}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: null, id: null, name: "" })}>
        <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Confirmar eliminación</DialogTitle>
          </DialogHeader>
          <p className="text-slate-300 text-sm">
            {deleteConfirm.type === "location"
              ? `¿Estás seguro de eliminar la ubicación "${deleteConfirm.name}"? Se eliminarán también los servicios asociados.`
              : `¿Estás seguro de eliminar el servicio "${deleteConfirm.name}"?`}
          </p>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, type: null, id: null, name: "" })} className="border-[#1E1E5F] text-slate-300" data-testid="delete-cancel-btn">
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (deleteConfirm.type === "service") handleDeleteService(deleteConfirm.id);
                else if (deleteConfirm.type === "location") handleDeleteLocation(deleteConfirm.id);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-confirm-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDetailPage;
